import React, { createContext, useContext, useEffect, useRef } from "react";
import { useScroll } from "framer-motion";
import Lenis from "lenis";

const ScrollCtx = createContext(null);
const MouseCtx = createContext(null);

export const useScrollCtx = () => useContext(ScrollCtx);
export const useMouseCtx = () => useContext(MouseCtx);

export const AppProviders = ({ children }) => {
  const { scrollYProgress } = useScroll();
  const lenisRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });

  // Lenis smooth scroll — drives the virtual camera timeline
  useEffect(() => {
    const lenis = new Lenis({
      lerp: 0.085,
      smoothWheel: true,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.5,
    });
    lenisRef.current = lenis;
    if (typeof window !== "undefined") window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Single global mouse listener (normalized -1..1)
  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const scrollToProgress = (p) => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const target = Math.max(0, Math.min(1, p)) * max;
    if (lenisRef.current) lenisRef.current.scrollTo(target, { duration: 1.7 });
    else window.scrollTo({ top: target, behavior: "smooth" });
  };

  return (
    <ScrollCtx.Provider value={{ progress: scrollYProgress, scrollToProgress }}>
      <MouseCtx.Provider value={mouse}>{children}</MouseCtx.Provider>
    </ScrollCtx.Provider>
  );
};
